import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ReactNode } from "react";

interface DeleteConfirmationDialogProps {
  /**
   * Controls whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when the open state changes
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Callback when the delete action is confirmed
   */
  onConfirm: () => void;
  /**
   * The type of entity being deleted (e.g., "Item", "Company", "Quote")
   */
  entityType: string;
  /**
   * The name/identifier of the item being deleted, displayed in the description.
   * Can be a string or ReactNode for custom formatting.
   */
  itemName?: string | ReactNode;
  /**
   * Optional custom description. If not provided, a default description will be used.
   */
  description?: ReactNode;
}

/**
 * A reusable delete confirmation dialog component that follows the AlertDialog pattern.
 * Used across controllers to confirm destructive delete actions.
 */
export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  entityType,
  itemName,
  description,
}: DeleteConfirmationDialogProps) {
  const defaultDescription = itemName ? (
    <>
      Are you sure you want to delete "{itemName}"? This action cannot be
      undone.
    </>
  ) : (
    <>
      Are you sure you want to delete this {entityType.toLowerCase()}? This
      action cannot be undone.
    </>
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {entityType}</AlertDialogTitle>
          <AlertDialogDescription>
            {description ?? defaultDescription}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
